import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Small anchored dropdown used by the header's assignee/status selects and the overflow menu.
 *
 * It now portals to the body, which the previous note named as the upgrade for the day a menu
 * had to escape a clip or scroll container. That day came: the order card sets `overflow:hidden`
 * to round its corners, so its own menu was clipped to a sliver, and the customer panel scrolls
 * on top of that.
 *
 * Position is measured from the trigger at open time and again on scroll or resize — a fixed
 * layer over a scrolling panel is wrong the moment the panel moves.
 */
export function Menu({
  trigger,
  children,
  align = 'left',
}: {
  trigger: (open: () => void) => ReactNode
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ top?: number; bottom?: number; left: number; right: number; maxHeight: number } | null>(null)

  // Measured before paint so the menu never appears at 0,0 for a frame first.
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const r = ref.current?.getBoundingClientRect()
      if (!r) return
      // Abre para cima quando embaixo não cabe: o raio das respostas rápidas fica no rodapé da
      // tela, e aberto para baixo a lista ficava fora da janela, impossível de usar.
      // ponytail: 320px é o "cabe" — a altura real só se sabe depois de desenhar.
      const below = window.innerHeight - r.bottom - 12
      const above = r.top - 12
      const up = below < 320 && above > below
      setBox({
        ...(up ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
        left: r.left,
        right: window.innerWidth - r.right,
        maxHeight: (up ? above : below) - 6,
      })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node
      // The menu itself lives in a portal, so `ref` no longer contains it — clicking an item
      // would otherwise close the menu before its own onClick ran.
      if (t instanceof Element && t.closest('[data-menu-portal]')) return
      if (ref.current && !ref.current.contains(t)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      {trigger(() => setOpen((o) => !o))}
      {open &&
        box &&
        createPortal(
          <div
            // Kept inside the same ref subtree for the outside-click check would be simpler, but
            // a portal breaks that containment — so the handler below checks the menu too.
            data-menu-portal=""
            className="pop"
            style={{
              position: 'fixed',
              top: box.top,
              bottom: box.bottom,
              maxHeight: box.maxHeight,
              left: align === 'left' ? box.left : undefined,
              right: align === 'right' ? box.right : undefined,
              minWidth: 210,
              zIndex: 220,
            }}
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </div>
  )
}
